import React, { createContext, ReactNode, useContext, useState } from "react";
import CacheStorage from "../../../service/storage/CacheStorage";
import StorageKeys from "../../../const/StorageKeys";
import UtilityHelper from "../../../service/utilities/UtilityHelper";
import SessionStorage from "../../../service/storage/SessionStorage";
import Loader from "../../../widget/common/ui/Loader";
import HomeGuestUserProvider from "./HomeGuestUserProvider";
import { BranchInfoGuest } from "../../../model/home_guest_user/GuestBranchInfoModel";

type HomeGuestUserContextType = {
  homeMenu: Array<HomeGuestUserMenuType>;
  branchDetail?: BranchInfoGuest | null;
  branchId?: string;
  branchFollowStatus?: string | "0";
  contactList?: Array<string> | undefined;
  socialLinks?: Array<SocialType> | undefined;
  getBranchDetail: (branchId?: string) => void;
};

export type HomeGuestUserMenuType = {
  name: string;
  title: string;
  icon: string;
};

export type SocialType = {
  name: string;
  icon: string;
  link: string;
};

const HomeGuestUserContext = createContext<
  HomeGuestUserContextType | undefined
>(undefined);

export const HomeGuestUserContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const homeMenu: Array<HomeGuestUserMenuType> = [
    { name: "general", title: "general", icon: "dept_chats" },
    { name: "feedback", title: "feedback", icon: "feedback" },
    { name: "products", title: "products", icon: "products" },
    { name: "email", title: "email", icon: "email" },
    { name: "app", title: "app", icon: "app_ic" },
    { name: "call", title: "call", icon: "call" },
    { name: "location", title: "location", icon: "location" },
  ];

  const branchId: string = CacheStorage.getValue(StorageKeys.branch_id);

  const [branchDetail, setBranchDetail] = useState<BranchInfoGuest | null>(
    null
  );
  const [contactList, setContactList] = useState<Array<string> | undefined>(
    undefined
  );
  const [socialLinks, setSocialLinks] = useState<Array<SocialType> | undefined>(
    undefined
  );

  const getBranchDetail = async (branchId: string = "") => {
    console.log("getBranchDetail", branchId);
    if (UtilityHelper.isNotEmpty(branchId)) {
      Loader.modal();
    }
    const branchInfoResult = await HomeGuestUserProvider.getBranchDetail(
      branchId
    );

    if (UtilityHelper.isNotEmpty(branchId)) {
      Loader.modal(false);
    }

    if (branchInfoResult != null) {
      const branchInfo: BranchInfoGuest =
        branchInfoResult?.result?.branch_info_guest!;

      const branchTitle: string = branchInfo?.title!;
      const branchLogo: string = branchInfo?.branch_logo_url!;
      const contactNumber: string = UtilityHelper.isNotEmpty(
        branchInfo?.phone_number!
      )
        ? `${branchInfo?.phone_code!}${branchInfo?.phone_number!}`
        : "";

      let socials: Array<SocialType> = [];

      if (UtilityHelper.isNotEmpty(branchInfo?.website!.trim())) {
        socials.push({
          name: "website",
          icon: "globe",
          link: branchInfo?.website!.trim(),
        });
      }

      (branchInfo.social_media_links ?? []).forEach((item) => {
        socials.push({
          name: item.key!,
          icon: item.key!,
          link: `${item.key!}.com/${item.value!}`,
        });
      });

      setBranchDetail(branchInfo);
      setContactList([contactNumber]);
      setSocialLinks(socials);

      // SESSIONS
      CacheStorage.saveValue(StorageKeys.branch_name, branchTitle);
      CacheStorage.saveValue(StorageKeys.branch_logo, branchLogo);

      SessionStorage.saveValue(
        StorageKeys.home_branch_product_url,
        branchInfo?.branch_products_details?.status! === "1"
          ? branchInfo?.branch_products_details?.media_details?.file_url!
          : ""
      );
    }
  };

  return (
    <HomeGuestUserContext.Provider
      value={{
        homeMenu,
        branchDetail,
        branchId,
        contactList,
        socialLinks,
        getBranchDetail,
      }}
    >
      {children}
    </HomeGuestUserContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const HomeGuestUserContextImport = () => {
  const context = useContext(HomeGuestUserContext);
  if (!context) {
    throw new Error(
      "HomeGuestUserContextImport must be used within a HomeGuestUserContextProvider"
    );
  }
  return context;
};
